import cn from "classnames"
import { useRef, useState } from "react"

import { useCreateKey } from "@/api/useAuthKeys"
import { Carousel, Modal, Steps } from "@/atoms"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useLogger } from "@/hooks/useLogger"
import { OSPlatform } from "@/icons"
import { OSStep } from "@/molecules/OSStep"
import { HowToUseTemplate } from "@/pages/IntegrationsPage/components/templates/HowToUseTemplate"
import { CreateKeyModal } from "@/pages/SettingsPage/components/organisms/AuthKeysTab"
import { CodeItem } from "../../molecules/CodeItem"

import { useDownloadIntegration } from "@/api/useIntegrations"
import { getKodemCLIExamples } from "@/api/useIntegrations.selector"
import { Button, Flex, Text } from "@/atoms"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { InfoCircleOutlined, KeyIcon, OSIcon, SyncOutlined } from "@/icons"
import { CopyButton } from "@/molecules/CopyButton"
import { downloadFileFromUrl } from "@/services/download"

import styles from "./HowToUseKodemCLI.module.scss"

import type { SourceIntegrationType } from "@/api/useAuthKeys.types"
import type { HowToUseProps } from "./HowToUseKodemCLI.types"

export const HowToUseKodemCLI = (props: HowToUseProps) => {
  const { t: tKodemCLI } = useTranslation("translation", {
    keyPrefix: "integrations.connect.kodemCLI"
  })
  const { t: tKodemCLIPlatform } = useTranslation("translation", {
    keyPrefix: "integrations.connect.kodemCLI.platform"
  })

  const { logger, EVENTS } = useLogger()
  const {
    OPENED_GENERATE_KEY,
    COMMAND_GENERATE_KEY,
    COMMAND_PLATFORM_SELECTED,
    COMMAND_GENERATE_LINK
  } = EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.KODEMCLI

  const [, modalContextHolder] = Modal.useModal()
  const [showModal, setShowModal] = useState<boolean | number>(false)
  const selfHosted = isSelfHosted()
  const [platform, setPlatform] = useState<OSPlatform>("darwin_arm64")
  const downloadCLIContext = useDownloadIntegration(props.name, platform)

  const platformOptions = useRef<{ value: OSPlatform; label: string }[] | null>(
    null
  )

  if (!platformOptions.current) {
    platformOptions.current = [
      "darwin_arm64",
      "darwin_amd64",
      "linux_arm64",
      "linux_amd64",
      "windows_amd64"
    ].map((platform) => ({
      value: platform as OSPlatform,
      label: tKodemCLIPlatform(platform)
    }))
  }

  const { items } = OSStep({
    classes: {
      select: styles.osSelect
    },
    options: platformOptions.current,
    onOSPlatformChange: (OSPlatform: OSPlatform) => {
      logger.info(COMMAND_PLATFORM_SELECTED, {
        platform: OSPlatform
      })

      setPlatform(OSPlatform)
    },
    getSteps: ({ OSPlatform: selectedPlatform }) => {
      const kodemCLIVersion = (props.extraInfo?.version || "latest").split(
        "-"
      )[0]

      const { response } = downloadCLIContext
      const { downloadUrl } = response?.data || {}

      const downloadCommandPlaceholder = `Generate command for ${tKodemCLIPlatform(selectedPlatform)}`
      const unixDownloadCommand = `sudo curl -o /usr/local/bin/kodem-cli \\
  '${downloadUrl}' && \\
  sudo chmod +x /usr/local/bin/kodem-cli`
      const windowsDownloadCommand = `curl.exe -o kodem-cli.exe "${downloadUrl}"`
      const downloadCommand =
        selectedPlatform === "windows_amd64"
          ? windowsDownloadCommand
          : unixDownloadCommand

      const pullCommand = `docker pull my-image:tag`
      const loginCommand = `kodem-cli auth login --api-key <KODEM_API_KEY>`
      const cloneCommand = `git clone <clone-url> /path/to/<repo>`

      const fetchDownloadUrl = () => {
        logger.info(COMMAND_GENERATE_LINK)
        return downloadCLIContext.refetch()
      }
      const kodemCliExamples = getKodemCLIExamples()
      const status = "process" as const

      return [
        {
          status,
          className: cn(selfHosted && styles.firstStepSelfHosted),
          title: tKodemCLI(
            `steps.download.${selfHosted ? "titleSelfHosted" : "title"}`
          ),
          content: selfHosted ? null : (
            <Flex vertical gap={0}>
              <Flex align="center" justify="space-between" gap={0}>
                <Text type="secondary">
                  {tKodemCLI("steps.download.command", {
                    version: kodemCLIVersion
                  })}
                </Text>
                <Button
                  icon={<SyncOutlined />}
                  size="small"
                  type="link"
                  onClick={fetchDownloadUrl}
                >
                  {downloadUrl ? "Refresh URL" : "Generate URL"}
                </Button>
              </Flex>
              <Flex vertical gap={10}>
                <div>
                  <pre className="code-block-step-how-to-use-template-ks">
                    <Text ellipsis>
                      <code>
                        {downloadUrl
                          ? downloadCommand
                          : downloadCommandPlaceholder}
                      </code>
                    </Text>
                    <CopyButton
                      disabled={!downloadUrl}
                      text={downloadCommand}
                      type="link"
                    />
                  </pre>
                  {downloadUrl && (
                    <Text type="secondary" className={styles.noteText}>
                      <InfoCircleOutlined />
                      <span>
                        <Trans>{tKodemCLI("steps.download.note")}</Trans>
                      </span>
                    </Text>
                  )}
                </div>
                <Flex vertical gap={4}>
                  <Text type="secondary">
                    {tKodemCLI("steps.download.direct", {
                      version: kodemCLIVersion
                    })}
                  </Text>
                  <div className="button-wrapper-step-how-to-use-template-ks">
                    <Button
                      type="secondary"
                      className="text-transform-initial-ks"
                      icon={<OSIcon platform={selectedPlatform} />}
                      onClick={async () => {
                        const context = await fetchDownloadUrl()
                        const url = context.data?.data.downloadUrl

                        if (url)
                          downloadFileFromUrl({
                            url,
                            filename: "kodem-cli"
                          })
                      }}
                    >
                      {tKodemCLI("steps.download.downloadKey", {
                        platform: tKodemCLIPlatform(selectedPlatform)
                      })}
                    </Button>
                  </div>
                </Flex>
              </Flex>
            </Flex>
          )
        },
        {
          status,
          title: tKodemCLI("steps.generateKey.title"),
          content: (
            <div className="description-step-how-to-use-template-ks">
              <Text type="secondary">
                {tKodemCLI("steps.generateKey.description")}
              </Text>
              <Flex vertical gap={8}>
                <div className="button-wrapper-step-how-to-use-template-ks">
                  <Button
                    type="secondary"
                    icon={<KeyIcon />}
                    onClick={() => {
                      logger.info(OPENED_GENERATE_KEY)
                      setShowModal(Date.now())
                    }}
                  >
                    {tKodemCLI("steps.generateKey.buttonText")}
                  </Button>
                </div>
                <Flex vertical gap={0}>
                  <Text type="secondary">
                    {tKodemCLI("steps.generateKey.login")}
                  </Text>
                  <pre className="code-block-step-how-to-use-template-ks">
                    <code>{loginCommand}</code>
                    <CopyButton text={loginCommand} type="link" />
                  </pre>
                </Flex>
              </Flex>
            </div>
          )
        },
        {
          status,
          title: tKodemCLI("steps.prepare.title"),
          content: (
            <Flex vertical gap={12}>
              <CodeItem
                titleLine1={tKodemCLI("steps.prepare.image")}
                codeSnippet={pullCommand}
              />
              <CodeItem
                titleLine1={tKodemCLI("steps.prepare.codeRepoScan")}
                codeSnippet={cloneCommand}
              />
            </Flex>
          )
        },
        {
          status,
          title: tKodemCLI("steps.scan.title"),
          content: (
            <div className="description-step-how-to-use-template-ks">
              <Text type="secondary">
                <Trans>
                  {tKodemCLI("steps.scan.description", { count: 7 })}
                </Trans>
              </Text>
              <Carousel arrows infinite={false}>
                {kodemCliExamples.map((sample, index) => (
                  <CodeItem
                    key={index}
                    {...sample} // titleLine1, titleLine2, infoLine, codeSnippet, withCopy
                    titleLine1={`<strong>Example ${index + 1}:</strong> ${sample.titleLine1}`}
                    withCopy
                  />
                ))}
              </Carousel>
            </div>
          )
        }
      ]
    }
  })

  return (
    <>
      <HowToUseTemplate selfHosted={selfHosted} {...props}>
        <Steps size="small" theme="dark" orientation="vertical" items={items} />
      </HowToUseTemplate>
      <CreateKeyModal
        key={`${showModal}`}
        open={!!showModal}
        useCreateKey={useCreateKey}
        logCreateKey={(keyName) => {
          logger.info(COMMAND_GENERATE_KEY, { keyName })
        }}
        keyType={"API_KEYS"}
        onClose={() => {
          setShowModal(false)
        }}
        sourceIntegration={props.name as SourceIntegrationType}
      />
      {modalContextHolder}
    </>
  )
}
